import { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import "@/App.css";
import { Heart, Leaf, ArrowRight, Check, Sparkles, Mail, User, ShoppingBag, Menu, X } from "lucide-react";
import axios from "axios";
import { BrowserRouter, Routes, Route, Link, useNavigate, Navigate, useLocation } from "react-router-dom";
import Shop from "@/pages/Shop";
import NotFound from "@/pages/NotFound";
import PrintScripts from "@/pages/PrintScripts";
import CardDeck from "@/pages/CardDeck";
import InstagramPosts from "@/pages/InstagramPosts";
import ThankYou from "@/pages/ThankYou";
import CardDownloads from "@/pages/CardDownloads";
import CardFront from "@/pages/CardFront";
import EmergencyCalmKit from "@/pages/EmergencyCalmKit";
import BoundaryBuilder from "@/pages/BoundaryBuilder";
import BoundaryLanding from "@/pages/BoundaryLanding";
import AcronymDictionary from "@/pages/AcronymDictionary";
import CodeBuilder from "@/pages/CodeBuilder";
import RefundPolicy from "@/pages/RefundPolicy";
import StripeLogos from "@/pages/StripeLogos";
import PinterestTemplates from "@/pages/PinterestTemplates";
import CardPulls from "@/pages/CardPulls";
import CardDraw from "@/pages/CardDraw";
import CardGenerator from "@/pages/CardGenerator";
import CardsRaw from "@/pages/CardsRaw";
import TodaysPullIG from "@/pages/TodaysPullIG";
import BlogMessyMiddle from "@/pages/BlogMessyMiddle";
import BlogYouFeedTheBaby from "@/pages/BlogYouFeedTheBaby";
import BlogIndex from "@/pages/BlogIndex";
import { ArticleList, ArticlePage, articles } from "@/pages/Blog";
import Distribute from "@/pages/Distribute";
import RecordingTemplate from "@/pages/RecordingTemplate";
import YesNoWait from "@/pages/YesNoWait";
import About from "@/pages/About";
import TapInDraftV2 from "@/pages/TapInDraftV2";
import TapInDraftV3 from "@/pages/TapInDraftV3";
import PinTapIn from "@/pages/PinTapIn";
import PostTapIn from "@/pages/PostTapIn";
import SocialHub from "@/pages/SocialHub";
import LinkInBio from "@/pages/LinkInBio";
import WorkbookOptin from "@/pages/WorkbookOptin";
import WorkbookSuccess from "@/pages/WorkbookSuccess";
import FreeResources from "@/pages/FreeResources";
import Contact from "@/pages/Contact";
import ContentBank from "@/pages/ContentBank";
import VoicePack from "@/pages/VoicePack";
import VoicePrompt from "@/pages/VoicePrompt";
import CoworkTest from "@/pages/CoworkTest";
import CalendarGallery from "@/pages/CalendarGallery";
import TapInCalendar from "@/pages/TapInCalendar";
import EmailDrafts from "@/pages/EmailDrafts";
import FunnelStats from "@/pages/FunnelStats";
import ShareCardsPreview from "@/pages/ShareCardsPreview";
import Commands from "@/pages/Commands";
import YouTubeBanner from "@/pages/YouTubeBanner";
import BookLaunchChecklist from "@/pages/BookLaunchChecklist";
import BookPage from "@/pages/BookPage";
import BookSuccess from "@/pages/BookSuccess";
import BuildYourOwn from "@/pages/BuildYourOwn";
import BuildYourOwnSuccess from "@/pages/BuildYourOwnSuccess";
import Start from "@/pages/Start";
import StartSuccess from "@/pages/StartSuccess";
import MediaInquiriesPage from "@/pages/MediaInquiriesPage";
import PressKit from "@/pages/PressKit";
import SampleSuccessPage from "@/pages/SampleSuccessPage";
import IGetToCoursePage from "@/pages/IGetToCoursePage";
import IGetToDeckPage from "@/pages/IGetToDeckPage";
import RewireDeckPage from "@/pages/RewireDeckPage";
import KindClearDeckPage from "@/pages/KindClearDeckPage";
import TheMethodPage from "@/pages/TheMethodPage";
import IGetToDeeperReturnPage from "@/pages/IGetToDeeperReturnPage";
import IGetToBundlePage from "@/pages/IGetToBundlePage";
import HygieneTrilogyPage from "@/pages/HygieneTrilogyPage";
import IGetToSuccessPage from "@/pages/IGetToSuccessPage";
import TempleStudioPage from "@/pages/TempleStudioPage";
import Home from "@/pages/Home";
import CourseBanner from "@/components/CourseBanner";
import ExitIntentPopup from "@/components/ExitIntentPopup";
import RouteSeo from "@/components/RouteSeo";
import { FreeToolsDropdown, FreeToolsMobileLinks } from "@/components/FreeToolsMenu";
import CredibilityStrip from "@/components/CredibilityStrip";
import TempleQuote from "@/components/TempleQuote";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Header Component with mobile hamburger menu (so About Temple + Shop All + The Book
// + AI for Kids + AI for Women are all reachable on mobile, not just desktop)
export const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const closeMenu = () => setMenuOpen(false);

  const navLinks = [
    { to: "/book", label: "The Book", testid: "nav-book", primary: true },
    { to: "/build-your-own", label: "Build Your Own", testid: "nav-build-your-own" },
    // Shop temporarily hidden until P1 legacy Stripe link migration (dead buy-links).
    { to: "/about", label: "About Temple", testid: "nav-about" },
    { to: "/blog", label: "Blog", testid: "nav-blog" },
    { to: "/contact", label: "Contact", testid: "nav-contact" },
  ];

  return (
    <header
      data-testid="main-header"
      className="print:hidden fixed top-0 w-full z-50 bg-white/80 backdrop-blur-xl border-b border-[#CCFBF1]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="logo-link" onClick={closeMenu}>
          <img
            src="/downloads/MBSH-logo-TM-e4476e.png"
            alt="Mind Body Spirit HYGIENE™ Tools"
            className="h-12 w-auto"
          />
        </Link>

        {/* DESKTOP NAV — visible md+ */}
        <nav className="hidden md:flex items-center gap-3 md:gap-6">
          {navLinks.slice(0, 5).map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className={`font-sans text-[#068fa2] hover:text-[#25a1c2] transition-colors ${l.primary ? "font-medium" : ""}`}
              data-testid={l.testid}
            >
              {l.label}
            </Link>
          ))}
          <FreeToolsDropdown testidPrefix="main-" />
          <Link
            to="/book"
            className="inline-flex items-center gap-2 bg-[#068fa2] hover:bg-[#042F2E] text-white rounded-full px-5 py-2.5 font-sans font-medium text-sm transition-all hover:-translate-y-0.5 shadow-md hover:shadow-lg"
            data-testid="nav-buy-book"
          >
            <ShoppingBag className="w-4 h-4" />
            Buy the Book
          </Link>
        </nav>

        {/* MOBILE — Buy button + hamburger menu */}
        <div className="flex md:hidden items-center gap-3">
          <Link
            to="/book"
            className="inline-flex items-center gap-1.5 bg-[#068fa2] hover:bg-[#042F2E] text-white rounded-full px-4 py-2 font-sans font-medium text-xs transition-all shadow-md"
            data-testid="nav-buy-book-mobile"
          >
            <ShoppingBag className="w-3.5 h-3.5" />
            Buy Book
          </Link>
          <button
            onClick={() => setMenuOpen((v) => !v)}
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            data-testid="mobile-menu-toggle"
            className="p-2 rounded-md text-[#042F2E] hover:bg-[#CCFBF1] transition-colors"
          >
            {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* MOBILE MENU DRAWER */}
      {menuOpen && (
        <div
          data-testid="mobile-menu-drawer"
          className="md:hidden bg-white border-t border-[#CCFBF1] shadow-lg max-h-[80vh] overflow-y-auto"
        >
          <nav className="px-6 py-4 flex flex-col gap-1">
            <FreeToolsMobileLinks onNavigate={closeMenu} testidPrefix="main-" />
            {navLinks.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={closeMenu}
                data-testid={`mobile-${l.testid}`}
                className="font-sans text-[#042F2E] hover:bg-[#CCFBF1] active:bg-[#CCFBF1] py-3 px-3 rounded-md transition-colors text-base font-medium border-b border-[#CCFBF1]/40 last:border-b-0"
              >
                {l.label}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
};

// Hero Section
const Hero = () => {
  const navigate = useNavigate();

  return (
    <section 
      data-testid="hero-section"
      className="min-h-screen pt-24 pb-16 md:pt-32 md:pb-24 relative overflow-hidden"
    >
      <div className="absolute inset-0 bg-gradient-to-br from-[#efeae7] via-white to-[#F0F5F4]" />
      
      {/* Decorative Elements */}
      <div className="absolute top-20 right-0 w-1/2 h-full opacity-20">
        <img 
          src="https://images.unsplash.com/photo-1610112839736-c9bddb0d98c8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwxfHxzdW5yaXNlJTIwb2NlYW4lMjBjYWxtfGVufDB8fHx8MTc3NTc2NDUzN3ww&ixlib=rb-4.1.0&q=85"
          alt="Calm ocean at sunrise"
          className="w-full h-full object-cover"
        />
      </div>
      
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        <div className="max-w-3xl">
          <p className="font-sans text-sm tracking-[0.2em] uppercase text-[#25a1c2] font-medium mb-3" data-testid="hero-overline">
            The Mind Body Spirit HYGIENE™ Method
          </p>
          <p className="font-serif text-xl md:text-2xl italic text-[#068fa2] mb-6" data-testid="hero-sub-overline">
            A whole new way of living.
          </p>
          
          <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl tracking-tighter leading-[1.05] text-[#042F2E] mb-6" data-testid="hero-headline">
            Stop Saying<br />
            <span className="text-[#475569] italic">"I Have To."</span><br />
            <span className="text-[#25a1c2]">Start LIVING</span> "I GET TO."
          </h1>
          
          <p className="font-sans text-xl md:text-2xl text-[#068fa2] italic font-medium leading-relaxed mb-6 max-w-xl" data-testid="hero-tagline">
            Real tools, for real women, in the messy middle.
          </p>

          <p className="font-sans text-lg md:text-xl text-[#475569] leading-relaxed mb-4 max-w-xl" data-testid="hero-description">
            I'm Temple Franklin — founder of the Mind Body Spirit HYGIENE™ method. Printable workbooks, boundary scripts, and calm kits designed as daily non-negotiable practices for conscious women ready to reclaim their agency.
          </p>
          <p className="font-serif text-2xl md:text-3xl italic text-[#068fa2] mb-10 max-w-xl" data-testid="hero-welcome">
            Welcome home. 💛
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 mb-12">
            <a 
              href="#signup"
              className="inline-flex items-center justify-center gap-2 bg-[#25a1c2] hover:bg-[#068fa2] text-white rounded-full px-8 py-4 font-sans font-medium transition-all hover:-translate-y-1 shadow-lg hover:shadow-xl"
              data-testid="hero-cta-btn"
            >
              Begin Your Journey
              <ArrowRight className="w-5 h-5" />
            </a>
            {/* "Shop Digital Products" button hidden until legacy old-account Stripe links on /shop are migrated. Restore this block to bring it back. */}
          </div>
          
          {/* Trust Indicators */}
          <div className="flex items-center gap-6 text-sm text-[#475569]">
            <div className="flex items-center gap-2">
              <Check className="w-5 h-5 text-[#25a1c2]" />
              <span>Free to join</span>
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-5 h-5 text-[#25a1c2]" />
              <span>Weekly insights</span>
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-5 h-5 text-[#25a1c2]" />
              <span>Unsubscribe anytime</span>
            </div>
          </div>

          {/* Credibility Strip — E-E-A-T proof */}
          <div className="mt-10 pt-8 border-t border-[#CCFBF1] max-w-2xl">
            <CredibilityStrip variant="light" compact />
          </div>
        </div>
      </div>
    </section>
  );
};

// 4 Phases Journey Section
const JourneySection = () => {
  const phases = [
    {
      number: "01",
      title: "Foundation",
      weeks: "Weeks 1-13",
      description: "Discover the core 'I GET TO' concept, understand the neuroscience behind mindset shifts, and build your daily practice.",
      icon: <Leaf className="w-6 h-6" />
    },
    {
      number: "02",
      title: "Identity",
      weeks: "Weeks 14-26",
      description: "Redefine who you are. Release old stories, reclaim your agency, and embrace being a work in progress.",
      icon: <Heart className="w-6 h-6" />
    },
    {
      number: "03",
      title: "Relationships",
      weeks: "Weeks 27-39",
      description: "Apply 'I GET TO' to your connections. Set boundaries, practice forgiveness, and choose your circle wisely.",
      icon: <Sparkles className="w-6 h-6" />
    },
    {
      number: "04",
      title: "Purpose & Deep Healing",
      weeks: "Weeks 40-52",
      description: "Integrate everything into a life of meaning. Embrace your calling, trust the timing, and share your transformation.",
      icon: <ArrowRight className="w-6 h-6" />
    }
  ];

  return (
    <section 
      id="journey" 
      data-testid="journey-section"
      className="py-24 md:py-32 bg-[#F0F5F4]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-16">
          <p className="font-sans text-sm tracking-[0.2em] uppercase text-[#25a1c2] font-medium mb-4">
            Your Transformation Path
          </p>
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl tracking-tight text-[#042F2E] mb-6">
            Four Phases to Freedom
          </h2>
          <p className="font-sans text-lg text-[#068fa2] max-w-2xl mx-auto">
            Each phase builds upon the last, guiding you through a complete transformation 
            of how you see yourself, your relationships, and your purpose.
          </p>
        </div>
        
        <div className="grid md:grid-cols-2 gap-6 md:gap-8">
          {phases.map((phase, index) => (
            <div 
              key={index}
              data-testid={`phase-card-${index + 1}`}
              className="bg-white p-8 md:p-10 rounded-3xl shadow-sm border border-[#CCFBF1] hover:shadow-md transition-all hover:-translate-y-1 group"
            >
              <div className="flex items-start justify-between mb-6">
                <span className="font-serif text-6xl text-[#CCFBF1] group-hover:text-[#25a1c2] transition-colors">
                  {phase.number}
                </span>
                <div className="w-12 h-12 rounded-full bg-[#F0F5F4] flex items-center justify-center text-[#25a1c2]">
                  {phase.icon}
                </div>
              </div>
              <h3 className="font-serif text-2xl md:text-3xl tracking-tight text-[#068fa2] mb-2">
                {phase.title}
              </h3>
              <p className="font-sans text-sm text-[#25a1c2] font-medium mb-4">
                {phase.weeks}
              </p>
              <p className="font-sans text-[#475569] leading-relaxed">
                {phase.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Email Signup Section
const SignupSection = () => {
  const [formData, setFormData] = useState({ firstName: "", email: "" });
  const [status, setStatus] = useState({ type: "", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: "", message: "" });

    try {
      await axios.post(`${API}/subscribe`, formData);
      setFormData({ firstName: "", email: "" });
      navigate("/thank-you");
    } catch (error) {
      setStatus({ type: "error", message: "Something went wrong. Please try again." });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section 
      id="signup" 
      data-testid="signup-section"
      className="py-24 md:py-32 bg-white relative overflow-hidden"
    >
      {/* Decorative Image */}
      <div className="absolute right-0 top-0 w-1/3 h-full opacity-10 hidden lg:block">
        <img 
          src="https://images.unsplash.com/photo-1728071485384-3602db0923ff?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwxfHxncmVlbiUyMGxlYXZlcyUyMHN1bmxpZ2h0fGVufDB8fHx8MTc3NTc2NDUzN3ww&ixlib=rb-4.1.0&q=85"
          alt="Green leaves in sunlight"
          className="w-full h-full object-cover"
        />
      </div>
      
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        <div className="max-w-2xl mx-auto text-center">
          <p className="font-sans text-sm tracking-[0.2em] uppercase text-[#25a1c2] font-medium mb-4">
            Your Weekly HYGIENE Practice
          </p>
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl tracking-tight text-[#042F2E] mb-6">
            Stop Saying "I Have To." Start LIVING "I GET TO."
          </h2>
          <p className="font-sans text-lg text-[#068fa2] mb-4">
            52 weeks. One thoughtful reframe a week. Delivered to your inbox by Temple Franklin — for conscious women in the messy middle who are ready to reclaim their agency.
          </p>
          <p className="font-sans text-[#25a1c2] font-medium mb-10">
            Sign up now and get a FREE printable 365-day "I GET TO" affirmation calendar.
          </p>
          
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="signup-form">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#25a1c2]" />
                <input 
                  type="text"
                  placeholder="Your First Name"
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  required
                  data-testid="signup-firstname-input"
                  className="w-full pl-12 pr-4 py-4 rounded-2xl border border-[#CCFBF1] bg-[#efeae7] font-sans text-[#042F2E] placeholder:text-[#475569] focus:outline-none focus:ring-2 focus:ring-[#25a1c2] focus:border-transparent transition-all"
                />
              </div>
              <div className="flex-1 relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#25a1c2]" />
                <input 
                  type="email"
                  placeholder="Your Email Address"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  data-testid="signup-email-input"
                  className="w-full pl-12 pr-4 py-4 rounded-2xl border border-[#CCFBF1] bg-[#efeae7] font-sans text-[#042F2E] placeholder:text-[#475569] focus:outline-none focus:ring-2 focus:ring-[#25a1c2] focus:border-transparent transition-all"
                />
              </div>
            </div>
            
            <button 
              type="submit"
              disabled={isSubmitting}
              data-testid="signup-submit-btn"
              className="w-full md:w-auto bg-[#25a1c2] hover:bg-[#068fa2] text-white rounded-full px-10 py-4 font-sans font-medium transition-all hover:-translate-y-1 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
            >
              {isSubmitting ? "Joining..." : "Start My 52-Week Journey"}
            </button>
            
            {status.message && (
              <div 
                data-testid="signup-status-message"
                className={`p-4 rounded-2xl ${
                  status.type === "success" 
                    ? "bg-[#F0FDF4] text-[#166534]" 
                    : "bg-[#FEF2F2] text-[#991B1B]"
                }`}
              >
                {status.message}
              </div>
            )}
          </form>
          
          <p className="font-sans text-sm text-[#475569] mt-6">
            Unsubscribe anytime.
          </p>
        </div>
      </div>
    </section>
  );
};

// ============================================================================
// CARD PULLS PROMO SECTION — Daily videos on YouTube
// ============================================================================
const CardPullsPromoSection = () => {
  return (
    <section className="py-16 md:py-20 px-6 bg-[#042F2E] text-white" data-testid="card-pulls-promo-section">
      <div className="max-w-5xl mx-auto">
        <div className="grid md:grid-cols-2 gap-10 md:gap-14 items-center">
          <div>
            <p className="font-sans text-xs tracking-[0.35em] uppercase text-[#d4af6a] font-medium mb-5" data-testid="card-pulls-promo-eyebrow">
              New on YouTube · Daily
            </p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[0.95] mb-6" data-testid="card-pulls-promo-headline">
              A card pull <span className="italic text-[#5EEAD4]">every morning.</span>
            </h2>
            <p className="font-serif italic text-xl text-white/80 leading-relaxed mb-8" data-testid="card-pulls-promo-body">
              One card. Pulled each morning.<br />
              For tapping into the energies of your day.<br />
              Grounded. Practical. Yours if it lands.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/card-pulls"
                className="bg-[#5EEAD4] hover:bg-white text-[#042F2E] font-sans font-semibold px-7 py-3 rounded-full text-sm tracking-wide transition-colors"
                data-testid="card-pulls-promo-cta-watch"
              >
                Watch today's pull →
              </Link>
              <a
                href="https://www.youtube.com/@MindBodySpiritHygiene"
                target="_blank"
                rel="noopener noreferrer"
                className="border border-white/30 hover:border-white text-white font-sans font-semibold px-7 py-3 rounded-full text-sm tracking-wide transition-colors"
                data-testid="card-pulls-promo-cta-subscribe"
              >
                Subscribe on YouTube
              </a>
            </div>
          </div>

          <div className="relative" data-testid="card-pulls-promo-visual">
            <div className="aspect-square rounded-3xl bg-gradient-to-br from-[#068fa2] via-[#042F2E] to-[#1a4d4c] shadow-2xl overflow-hidden relative flex items-center justify-center">
              <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(circle at 30% 30%, rgba(212, 175, 106, 0.15) 0%, transparent 60%)" }} />
              <div className="relative w-3/4 h-3/4 flex items-center justify-center">
                <div className="absolute w-32 h-48 bg-[#efeae7] rounded-lg shadow-xl transform -rotate-12 -translate-x-8 border border-[#d4af6a]/40" />
                <div className="absolute w-32 h-48 bg-[#efeae7] rounded-lg shadow-xl transform rotate-3 border border-[#d4af6a]/40" />
                <div className="absolute w-32 h-48 bg-[#efeae7] rounded-lg shadow-xl transform rotate-12 translate-x-8 border border-[#d4af6a]/40" />
                <div className="relative w-32 h-48 bg-[#042F2E] border-2 border-[#d4af6a] rounded-lg shadow-2xl flex flex-col items-center justify-center">
                  <span className="text-[#d4af6a] text-3xl mb-2">✦</span>
                  <span className="font-serif italic text-[#efeae7] text-xs tracking-widest text-center px-2">DAILY<br />TAP IN<br />PULL</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Sneak Peek Section
const SneakPeekSection = () => {
  return (
    <section 
      data-testid="sneak-peek-section"
      className="py-24 md:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-12">
          <p className="font-sans text-sm tracking-[0.2em] uppercase text-[#25a1c2] font-medium mb-4">
            A Taste of What's Coming
          </p>
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl tracking-tight text-[#042F2E] mb-6">
            Sneak Peek: Week 1
          </h2>
          <p className="font-sans text-lg text-[#068fa2] max-w-2xl mx-auto">
            Here's a glimpse of your first email. Every week, you'll receive thoughtful 
            guidance like this, designed to shift your perspective one step at a time.
          </p>
        </div>
        
        <div className="max-w-3xl mx-auto">
          <div className="bg-[#efeae7] border border-[#CCFBF1] rounded-3xl p-8 md:p-12 shadow-sm">
            {/* Email Header */}
            <div className="border-b border-[#CCFBF1] pb-6 mb-6">
              <p className="font-sans text-sm text-[#475569] mb-2">
                <span className="font-medium">Subject:</span> You made it here. That matters.
              </p>
              <p className="font-sans text-sm text-[#475569]">
                <span className="font-medium">From:</span> Temple
              </p>
            </div>
            
            {/* Email Content */}
            <div className="space-y-4 font-sans text-[#068fa2] leading-relaxed">
              <p className="text-lg">
                Hey <span className="text-[#25a1c2] font-medium">[Your Name]</span>,
              </p>
              <p>
                Welcome to the beginning of something powerful.
              </p>
              <p>
                You're here because something in you is ready for a shift. Maybe you're tired of 
                feeling weighed down by everything you "have to" do. Maybe you're curious about 
                what it would feel like to move through your days with more lightness, more agency.
              </p>
              <p>
                Here's the core of what we'll practice together:
              </p>
              <p className="text-xl font-serif text-[#25a1c2] italic pl-4 border-l-4 border-[#25a1c2]">
                "I GET TO."
              </p>
              <p>
                Three simple words. But they change everything.
              </p>
              <p>
                This week, I want you to start something simple, yet profound: Take one task you've been dreading—
                something you "have to" do—and reframe it.
              </p>
              <p className="font-medium text-[#25a1c2] italic">
                Remember: If you want things to change, You have to change.
              </p>
              <div className="bg-white rounded-2xl p-6 my-6">
                <p className="font-medium text-[#042F2E] mb-2">Your Week 1 Practice:</p>
                <p className="text-[#475569]">
                  Instead of "I have to go to work," use your new hygiene tool: "I get to go to work because..."
                  <br />
                  Instead of "I have to cook dinner," practice your new hygiene tool: "I get to cook dinner because..."
                </p>
              </div>
              <p>
                See you next week.
              </p>
              <p className="font-medium text-[#25a1c2]">
                — Temple
              </p>
            </div>
          </div>
          
          <div className="text-center mt-8">
            <a 
              href="#signup"
              className="inline-flex items-center gap-2 bg-[#25a1c2] hover:bg-[#068fa2] text-white rounded-full px-8 py-4 font-sans font-medium transition-all hover:-translate-y-1 shadow-lg hover:shadow-xl"
              data-testid="sneak-peek-cta-btn"
            >
              Get Week 1 in Your Inbox
              <ArrowRight className="w-5 h-5" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

// About Temple Section
const AboutSection = () => {
  return (
    <section 
      id="about" 
      data-testid="about-section"
      className="py-24 md:py-32 bg-[#F0F5F4]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-3xl mx-auto text-center">
          <p className="font-sans text-sm tracking-[0.2em] uppercase text-[#25a1c2] font-medium mb-4">
            Your Guide
          </p>
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl tracking-tight text-[#042F2E] mb-8">
            Meet Temple
          </h2>
          
          {/* Decorative Element */}
          <div className="flex justify-center mb-8">
            <div className="w-20 h-20 rounded-full bg-[#25a1c2]/10 flex items-center justify-center">
              <Heart className="w-10 h-10 text-[#25a1c2]" />
            </div>
          </div>
          
          <div className="space-y-6 font-sans text-lg text-[#068fa2] leading-relaxed">
            <p className="font-serif text-2xl italic text-[#25a1c2]">
              If you woke up this morning and didn't quite recognize the woman in the mirror — keep reading.
            </p>
            <p>
              Maybe you're divorced, or wish you were. Maybe a kid won't call you back. Maybe your bloodwork keeps coming back <em>"normal"</em> while your body screams something else. Maybe you said yes when you meant no — again — and you're sitting in the cost of it. Maybe you're tired in a way coffee can't touch.
            </p>
            <p className="font-serif italic text-[#042F2E]">
              I know. I've been her.
            </p>
            <p>
              I'm <strong>Temple Franklin</strong> — founder of the Mind Body Spirit HYGIENE™ Method. I'm a 15-year broadcast veteran, HeartMath certified, an evidential medium, Intuitive, and a woman who spent <em>fifty years</em> searching for a home within herself.
            </p>
            <p>
              Adopted at birth. No elder to name the gifts I was born with — so I shut them down from fear. Married for 22 years to a life that was eroding emotionally for years. Mother to three daughters and the slow painful grief of estrangement. Brought down to the studs by undeniable grief, Hashimoto's, a breast cancer scare.
            </p>
            <p>
              I trained for three years with a renowned meditation teacher. I sat with mediums. I read every book. I researched Mind, Body, Spirit for over twenty years — <em>because no one had given me the language.</em>
            </p>
            <p>
              And then one ordinary morning, at the kitchen trash can, three words landed in the middle of my chest… <span className="font-serif italic text-[#25a1c2] font-medium">I GET TO.</span>
            </p>
            <p>
              I get to take out this trash — because I have a home. Because I am alive and able. Because the woman I used to be would have given anything to be standing where I'm standing right now.
            </p>
            <p>
              That moment cracked open everything that became this work. The Mind Body Spirit HYGIENE™ Method. The A.C.T. NOW pattern — Awareness · Clarity · Tapped In · No Opportunity Wasted. Every tool inside it.
            </p>
            <p className="text-[#042F2E] font-medium">
              I built this because <em className="text-[#25a1c2] not-italic font-semibold">no elder was in the room when I needed one.</em> So I became her. I assembled the daily practice I wished someone had handed me at twenty, at thirty, at the studs.
            </p>
            <p>
              This is the inheritance I had to build from scratch — <strong>real tools, for real women, in the messy middle.</strong> Not transformation. Not hustle. Not high-vibe. Just <em>hygiene</em>. Daily. Non-negotiable. Yours.
            </p>
            <p className="text-[#25a1c2] font-medium italic">
              Whatever brought you to this page — it brought you home.
            </p>
            <p className="font-serif text-xl italic text-[#042F2E]">
              Welcome home. 💛
            </p>
            <p className="text-[#475569] text-sm">
              — Temple Franklin & the Divine Team
            </p>
          </div>
          
          <div className="mt-10">
            <a 
              href="#signup"
              className="inline-flex items-center gap-2 bg-[#25a1c2] hover:bg-[#068fa2] text-white rounded-full px-8 py-4 font-sans font-medium transition-all hover:-translate-y-1 shadow-lg hover:shadow-xl"
              data-testid="about-cta-btn"
            >
              Begin Your Journey Home
              <ArrowRight className="w-5 h-5" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer — the single site-wide footer (rendered once, after <Routes>)
const FOOTER_LINK = "font-sans text-white/70 hover:text-white text-sm transition-colors";
export const Footer = () => {
  return (
    <footer data-testid="main-footer" className="py-12 bg-[#042F2E] print:hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <Link to="/" data-testid="footer-logo-link">
            <img src="/downloads/MBSH-logo-TM-e4476e.png" alt="Mind Body Spirit HYGIENE™ Tools" className="h-16 w-auto" />
          </Link>
          <p className="font-sans text-[#5EEAD4] text-sm text-center md:text-left leading-relaxed" data-testid="footer-copyright">
            © 2026 Temple Franklin. All rights reserved.
            <br />
            Mind Body Spirit HYGIENE™ is a trademark of Temple Franklin.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3">
            <a href="https://instagram.com/mindbodyspirithygienetools" target="_blank" rel="noopener noreferrer" className={FOOTER_LINK} data-testid="footer-instagram">Instagram</a>
            <a href="https://www.youtube.com/@MindBodySpiritHygiene" target="_blank" rel="noopener noreferrer" className={FOOTER_LINK} data-testid="footer-youtube">YouTube</a>
            <Link to="/blog" className={FOOTER_LINK} data-testid="footer-blog">Blog</Link>
            <Link to="/contact" className={FOOTER_LINK} data-testid="footer-contact">Contact</Link>
            <Link to="/media-inquiries" className={FOOTER_LINK} data-testid="footer-press">Press</Link>
            <Link to="/refund-policy" className={FOOTER_LINK} data-testid="footer-refund-policy">All Sales Final</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Landing Page
function LandingPage() {
  return (
    <>
      <Helmet>
        <title>The Mind Body Spirit HYGIENE™ Method by Temple Franklin | A Whole New Way of Living</title>
        <meta
          name="description"
          content="Meet Temple Franklin and the Mind Body Spirit HYGIENE™ Method — a whole new way of living. Her story, the journey, the practice, the testimonials. Real tools for real women in the messy middle."
        />
        <link rel="canonical" href="https://mindbodyspirithygienetools.com/home" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="The Mind Body Spirit HYGIENE™ Method by Temple Franklin — A Whole New Way of Living" />
        <meta
          property="og:description"
          content="Temple Franklin's story, the journey, the practice. Real tools for real women in the messy middle."
        />
        <meta property="og:url" content="https://mindbodyspirithygienetools.com/home" />
      </Helmet>
      <Header />
      <main>
        <Hero />
        <section className="px-6 py-10 md:py-14" style={{ background: "#F8F5F3" }} data-testid="home-course-promo">
          <CourseBanner product="bundle" variant="card" testId="home-course-banner" />
        </section>
        <JourneySection />
        <CardPullsPromoSection />
        <SneakPeekSection />
        <TempleQuote
          quote="The buck stops here."
          variant="hero"
          theme="teal"
          id="quote-buck-stops-here"
        />
        <SignupSection />
        <AboutSection />
        <TempleQuote
          quote="For every woman who picks up this work, an entire family system shifts."
          variant="hero"
          theme="cream"
          id="quote-family-shifts"
        />
        <TempleQuote
          quote="Stop abandoning yourself."
          variant="hero"
          theme="teal"
          id="quote-stop-abandoning"
        />
      </main>
    </>
  );
}

// Main App with Router
// Fires Meta Pixel PageView on every SPA route change (base pixel only auto-fires
// on the initial page load; React Router client-side nav is invisible to it).
function AnalyticsRouteListener() {
  const location = useLocation();
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (typeof window.fbq === "function") {
      window.fbq("track", "PageView");
    }
    // Also fire GA4 page_view for parity (gtag config has send_page_view: true
    // on initial load but not on client-side nav)
    if (typeof window.gtag === "function") {
      window.gtag("event", "page_view", {
        page_path: location.pathname + location.search,
        page_location: window.location.href,
      });
    }
  }, [location.pathname, location.search]);
  return null;
}

function SiteBookFooter() {
  const { pathname } = useLocation();
  const path = pathname.replace(/\/+$/, "") || "/";
  // Respect /book's no-nav layout and the /start funnel; link everywhere else.
  // Excluded from the $33 course pages: it contradicts the "no book required"
  // FAQ on the sales page and is the wrong cross-sell on the post-purchase page.
  if (path === "/book" || path === "/start" || path === "/build-your-own" || path === "/build-your-own-success" || path === "/press-kit") return null;
  return (
    <div data-testid="site-book-footer" className="bg-[#042F2E] px-4 py-6 text-center">
      <p className="text-[#efeae7] text-sm">
        New here?{" "}
        <Link
          to="/book"
          data-testid="footer-book-link"
          className="font-semibold text-[#e8b04b] underline underline-offset-2 hover:text-[#f0c46e] transition-colors"
        >
          Start with the book →
        </Link>
      </p>
    </div>
  );
}

const NO_FOOTER = ["/start", "/start-success", "/commands", "/distribute", "/studio", "/press-kit"];
function SiteFooter() {
  const { pathname } = useLocation();
  const path = pathname.replace(/\/+$/, "") || "/";
  return NO_FOOTER.includes(path) ? null : <Footer />;
}

function App() {
  return (
    <BrowserRouter>
      <AnalyticsRouteListener />
      <RouteSeo />
      <div className="App bg-[#efeae7]" data-testid="app-container">
        <ExitIntentPopup />
        <Routes>
          {/* HOMEPAGE — book-first single hero (testimonial + Temple's story).
              Built June 24, 2026 to stop overwhelming cold visitors.
              All other products live on /shop (compact horizontal-row catalog). */}
          <Route path="/" element={<Home />} />
          <Route path="/home" element={<LandingPage />} />
          <Route path="/shop" element={<Shop />} />
          {/* ─── FREE-OFFER FRONT DOOR — added June 10, 2026 ─── */}
          <Route path="/workbook" element={<WorkbookOptin />} />
          <Route path="/workbook-success" element={<WorkbookSuccess />} />
          <Route path="/free" element={<FreeResources />} />
          <Route path="/free-resources" element={<Navigate to="/free" replace />} />
          <Route path="/content/print" element={<PrintScripts />} />
          <Route path="/cards" element={<CardDeck />} />
          <Route path="/instagram" element={<InstagramPosts />} />
          <Route path="/thank-you" element={<ThankYou />} />
          <Route path="/card-downloads" element={<CardDownloads />} />
          <Route path="/card-front" element={<CardFront />} />
          <Route path="/emergency-calm-kit" element={<EmergencyCalmKit />} />
          <Route path="/boundary-builder" element={<BoundaryBuilder />} />
          <Route path="/boundaries" element={<BoundaryLanding />} />
          <Route path="/acronym-dictionary" element={<AcronymDictionary />} />
          <Route path="/code-builder" element={<CodeBuilder />} />
          <Route path="/refund-policy" element={<RefundPolicy />} />
          <Route path="/refunds" element={<RefundPolicy />} />
          <Route path="/all-sales-final" element={<RefundPolicy />} />
          <Route path="/brand-logos" element={<StripeLogos />} />
          <Route path="/pinterest-templates" element={<PinterestTemplates />} />
          <Route path="/card-pulls" element={<CardPulls />} />
          <Route path="/draw" element={<CardDraw />} />
          <Route path="/card-generator" element={<CardGenerator />} />
          <Route path="/cards-raw" element={<CardsRaw />} />
          <Route path="/todays-pull-ig" element={<TodaysPullIG />} />
          <Route path="/blog" element={<BlogIndex />} />
          <Route path="/blog/the-messy-middle-is-the-door" element={<BlogMessyMiddle />} />
          <Route path="/blog/you-feed-the-baby" element={<BlogYouFeedTheBaby />} />
          <Route path="/blog" element={<ArticleList />} />
          {articles.filter(a => !a.hidden).map((a) => (
            <Route key={a.slug} path={`/blog/${a.slug}`} element={<ArticlePage slug={a.slug} />} />
          ))}
          <Route path="/distribute" element={<Distribute />} />
          <Route path="/studio" element={<RecordingTemplate />} />
          <Route path="/yes-no-wait" element={<YesNoWait />} />
          <Route path="/about" element={<About />} />
          <Route path="/tap-in-draft-v2" element={<TapInDraftV2 />} />
          <Route path="/tap-in-draft-v3" element={<TapInDraftV3 />} />
          <Route path="/pin/the-tap-in" element={<PinTapIn />} />
          <Route path="/post/the-tap-in" element={<PostTapIn />} />
          <Route path="/social" element={<SocialHub />} />
          <Route path="/links" element={<LinkInBio />} />
          <Route path="/link" element={<LinkInBio />} />
          <Route path="/connect" element={<LinkInBio />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/content-bank" element={<ContentBank />} />
          <Route path="/voice-pack" element={<VoicePack />} />
          <Route path="/voice-prompt" element={<VoicePrompt />} />
          <Route path="/cowork-test" element={<CoworkTest />} />
          <Route path="/calendar-gallery" element={<CalendarGallery />} />
          <Route path="/tap-in-calendar" element={<TapInCalendar />} />
          <Route path="/email-drafts" element={<EmailDrafts />} />
          <Route path="/funnel-stats" element={<FunnelStats />} />
          <Route path="/share-cards" element={<ShareCardsPreview />} />
          <Route path="/commands" element={<Commands />} />
          <Route path="/yt-banner" element={<YouTubeBanner />} />
          <Route path="/book-launch" element={<BookLaunchChecklist />} />
          <Route path="/book" element={<BookPage />} />
          <Route path="/book-success" element={<BookSuccess />} />
          <Route path="/build-your-own" element={<BuildYourOwn />} />
          <Route path="/build-your-own-success" element={<BuildYourOwnSuccess />} />
          <Route path="/free-chapter" element={<BookPage />} />
          <Route path="/free-sample" element={<BookPage />} />
          <Route path="/start" element={<Start />} />
          <Route path="/start-success" element={<StartSuccess />} />
          <Route path="/media-inquiries" element={<MediaInquiriesPage />} />
          <Route path="/press-kit" element={<PressKit />} />
          <Route path="/sample-success" element={<SampleSuccessPage />} />
          <Route path="/i-get-to-course" element={<IGetToCoursePage />} />
          <Route path="/i-get-to-deck" element={<IGetToDeckPage />} />
          <Route path="/hygiene-trilogy" element={<HygieneTrilogyPage />} />
          <Route path="/mini-library" element={<HygieneTrilogyPage />} />
          <Route path="/trilogy" element={<HygieneTrilogyPage />} />
          <Route path="/rewire-deck" element={<RewireDeckPage />} />
          <Route path="/rewire" element={<RewireDeckPage />} />
          <Route path="/kind-clear-deck" element={<KindClearDeckPage />} />
          <Route path="/kind-and-clear" element={<KindClearDeckPage />} />
          <Route path="/the-method" element={<TheMethodPage />} />
          <Route path="/method" element={<TheMethodPage />} />
          <Route path="/all-decks" element={<TheMethodPage />} />
          <Route path="/the-body-knows" element={<TheMethodPage />} />
          <Route path="/body-knows" element={<TheMethodPage />} />
          <Route path="/the-body-knows-deck" element={<TheMethodPage />} />
          <Route path="/true-north" element={<TheMethodPage />} />
          <Route path="/true-north-deck" element={<TheMethodPage />} />
          <Route path="/i-get-to-deeper-return" element={<IGetToDeeperReturnPage />} />
          <Route path="/i-get-to-bundle" element={<IGetToBundlePage />} />
          <Route path="/i-get-to-success" element={<IGetToSuccessPage />} />
          <Route path="/i-get-to-deeper-success" element={<IGetToSuccessPage />} />
          <Route path="/i-get-to-bundle-success" element={<IGetToSuccessPage />} />
          <Route path="/temple-studio" element={<TempleStudioPage />} />
          {/* Catch-all 404 — sets robots: noindex,nofollow at runtime to avoid soft-404 penalties */}
          <Route path="*" element={<NotFound />} />
        </Routes>
        <SiteBookFooter />
        <SiteFooter />
      </div>
    </BrowserRouter>
  );
}

export default App;
